/** Starts and stops nobody owns, kept here and sent to no service, read as a
 *  sentence (DEC-25).
 *
 * By reason, because each reason sends a person somewhere different: a
 * refusal is the card, a missing answer is an authority, and no decision at
 * all is a station that asked nobody.
 */

import type { UnownedCounts } from '../api'

export type { UnownedCounts }

/** What the service writes, in words. A reason this console has not heard of
 *  is shown as it arrives rather than folded into "other". */
const REASONS: Record<string, string> = {
  refused: 'refused',
  unknown: 'unknown card',
  timeout: 'no answer in time',
  no_decision: 'never asked',
  approved_unbound: 'approved, no stamp',
  // Approved by the Wall-E list, which names no payer (DEC-28).
  wall_e_list: 'on the Wall-E list, no payer',
}

/** Why an unstamped event was forwarded rather than kept, where DEC-25 asks
 *  for a count. */
const FORWARDED: Record<string, string> = {
  no_start_seen: 'stop with no start',
  no_transaction_id: 'no transaction id',
}

export function unownedTotal(counts: Record<string, number> | undefined): number {
  return Object.values(counts ?? {}).reduce((sum, n) => sum + n, 0)
}

/** Shown while the switch is on, and afterwards while anything it kept is
 *  still inside the day. Off and empty is a permanent zero nobody needs. */
export function unownedShown(counts: UnownedCounts | undefined): boolean {
  if (!counts) return false
  return counts.enabled || unownedTotal(counts.last_day) > 0
}

function byReason(counts: Record<string, number>, words = REASONS): string {
  const entries = Object.entries(counts).filter(([, n]) => n > 0)
  if (entries.length === 0) return 'none'
  // Largest first, then by reason so equal counts do not swap between renders.
  entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  return entries.map(([reason, n]) => `${n} ${words[reason] ?? reason}`).join(', ')
}

export function unownedCaption(counts: UnownedCounts): string {
  const parts = [`Last day: ${byReason(counts.last_day)}.`, `Last hour: ${byReason(counts.last_hour)}.`]
  if (unownedTotal(counts.forwarded_last_day) > 0) {
    parts.push(`Forwarded as before, last day: ${byReason(counts.forwarded_last_day ?? {}, FORWARDED)}.`)
  }
  if ((counts.no_event_time_last_day ?? 0) > 0) {
    parts.push(`${counts.no_event_time_last_day} with no event time, last day.`)
  }
  if ((counts.anchored_on_arrival_last_day ?? 0) > 0) {
    parts.push(`${counts.anchored_on_arrival_last_day} judged by arrival time, last day.`)
  }
  if (!counts.enabled) parts.push('Switched off.')
  return parts.join(' ')
}
